import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Observable, of, ReplaySubject} from "rxjs";
import {Invoice} from "../../../../shared/interface/invoice";
import {UserPermissions} from "../../../../shared/interface/user-permissions";
import {AuthService} from "../../../../shared/services/auth.service";
import {InvoiceService} from "../../../../shared/services/invoice.service";
import {MatDialog} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {Router} from "@angular/router";
import {debounceTime, delay, distinctUntilChanged, first, takeUntil, tap} from "rxjs/operators";
import {HttpErrorResponse} from "@angular/common/http";
import {
  CONTRACT_TYPE_MANDATE, CONTRACT_TYPE_MANDATE_COMPLETE_PACKAGE,
  CONTRACT_TYPE_MANDATE_ONLY_HEALTH, CONTRACT_TYPE_MANDATE_STUDENT,
  INVOICE_DOCUMENT_TYPE_CREDIT,
  INVOICE_STATUS_PAID,
  INVOICE_STATUS_REJECTED,
  INVOICE_STATUS_SENT_FOR_PAYMENT,
  INVOICE_STATUS_SUSPENDED,
  INVOICE_STATUS_TO_BE_CORRECTED,
  INVOICE_STATUS_TO_BE_REALIZED
} from '../../../../shared/services/dict.service';
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {validIban} from "../../../../shared/validator/iban-validator";
import {validNip} from "../../../../shared/validator/nip-validator";
import {Uuid} from "../../../../shared/interface/response/uuid";
import {FormHelperService} from "../../../../shared/services/form-helper.service";
import {FileBase64} from "../../../../shared/components/file-drag-and-drop/file-drag-and-drop.component";

@Component({
  selector: 'app-edit-invoice',
  templateUrl: './edit-invoice.component.html',
  styleUrls: ['./edit-invoice.component.scss']
})
export class EditInvoiceComponent implements OnInit, OnDestroy {
  @Output() closeModification = new EventEmitter<any>();
  @Input() invoice?: Observable<Invoice>;
  @Output() changed: EventEmitter<boolean> = new EventEmitter<boolean>();

  readonly INVOICE_STATUS_TO_BE_REALIZED = INVOICE_STATUS_TO_BE_REALIZED;
  readonly INVOICE_STATUS_PAID = INVOICE_STATUS_PAID;
  readonly INVOICE_STATUS_SUSPENDED = INVOICE_STATUS_SUSPENDED;
  readonly INVOICE_STATUS_REJECTED = INVOICE_STATUS_REJECTED;
  readonly INVOICE_STATUS_TO_BE_CORRECTED = INVOICE_STATUS_TO_BE_CORRECTED;
  readonly INVOICE_STATUS_SENT_FOR_PAYMENT = INVOICE_STATUS_SENT_FOR_PAYMENT;

  fileMaxSize = 7340032;
  invoiceObject: Invoice;
  invoiceForm: FormGroup;
  userPermissions: ReplaySubject<UserPermissions>;
  sortBy = 'billingMonth';
  invoiceFormValid = false;
  processingRequest = false;
  enableSaveButton = false;
  sellerNipRequired = true;
  errors: any = {};
  documentInvoiceNet = 0;
  documentInvoiceCredit = 0;
  documentInvoiceCharge = 0;

  numberControl: FormControl;
  netControl: FormControl;
  grossControl: FormControl;
  sellerCompanyNameControl: FormControl;
  sellerNipControl: FormControl;
  sellerStreetControl: FormControl;
  sellerHouseNumberControl: FormControl;
  sellerApartmentNumberControl: FormControl;
  sellerPostCityControl: FormControl;
  sellerZipCodeControl: FormControl;
  sellerCityControl: FormControl;
  sellerAccountNumberControl: FormControl;
  sellerEmailControl: FormControl;
  sellerPhoneControl: FormControl;
  vatInvoiceControl: FormControl;
  noteControl: FormControl;
  fileNameControl: FormControl;
  fileContentControl: FormControl;
  pdfNameControl: FormControl;
  createdAtControl: FormControl;

  private initialized: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  constructor(
    private auth: AuthService,
    private invoiceService: InvoiceService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private router: Router,
    private fb: FormBuilder,
    private formHelper: FormHelperService,

  ) {
    this.userPermissions = auth.getUserPermissions();
    this.invoiceForm = this.fb.group({
      number: this.numberControl = fb.control('', [Validators.required]),
      net: this.netControl = fb.control(''),
      gross: this.grossControl = fb.control(''),
      sellerCompanyName: this.sellerCompanyNameControl = fb.control('', [Validators.required]),
      sellerNip: this.sellerNipControl = fb.control('', [Validators.required, validNip()]),
      sellerStreet: this.sellerStreetControl = fb.control('', [Validators.required]),
      sellerHouseNumber: this.sellerHouseNumberControl = fb.control('',  [Validators.required]),
      sellerApartmentNumber: this.sellerApartmentNumberControl = fb.control(''),
      sellerPostCity: this.sellerPostCityControl = fb.control('',  [Validators.required]),
      sellerZipCode: this.sellerZipCodeControl = fb.control('', [Validators.required]),
      sellerCity: this.sellerCityControl = fb.control('', [Validators.required]),
      sellerAccountNumber: this.sellerAccountNumberControl = fb.control('', [Validators.required, validIban()]),
      sellerEmail: this.sellerEmailControl = fb.control('', [Validators.required]),
      phone: this.sellerPhoneControl = fb.control('', [Validators.required]),
      vatInvoice: this.vatInvoiceControl = fb.control(false),
      note: this.noteControl = fb.control(''),
      fileName: this.fileNameControl = fb.control(''),
      fileContent: this.fileContentControl = fb.control('' ),
      pdfName: this.pdfNameControl = fb.control('' ),
      createdAt: this.createdAtControl = fb.control('', [Validators.required]),
    });
  }

  ngOnInit(): void {
    this.invoiceForm.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        delay(0),
        tap(() => this.enableSaveButton = this.invoiceForm.valid)
      )
      .subscribe();

    if (this.invoice) {
      this.invoice
        .pipe(takeUntil(this.destroyed$))
        .subscribe((invoice: Invoice) => {
          this.invoiceObject = invoice;
          if ([CONTRACT_TYPE_MANDATE, CONTRACT_TYPE_MANDATE_ONLY_HEALTH, CONTRACT_TYPE_MANDATE_STUDENT,
            CONTRACT_TYPE_MANDATE_COMPLETE_PACKAGE].includes(invoice.contract?.type?.key) ) {
            this.sellerNipRequired = false;
            this.sellerNipControl.clearValidators();
            this.sellerNipControl.updateValueAndValidity();
          } else {
            this.sellerNipRequired = true;
            this.sellerNipControl.setValidators([Validators.required]);
            this.sellerNipControl.updateValueAndValidity();
          }
          this.grossControl.clearValidators();
          this.netControl.clearValidators();
          if (this.isMandateBill()) {
            this.grossControl.setValidators([Validators.required]);
            this.netControl.setValidators([Validators.required]);
          } else if (this.invoiceObject?.partner) {
            this.netControl.setValidators([Validators.required]);
          } else {
            this.grossControl.setValidators([Validators.required]);
          }
          this.netControl.updateValueAndValidity();
          this.grossControl.updateValueAndValidity();

          this.invoiceObject.documents.forEach((document) => {
            if (document.type.key === INVOICE_DOCUMENT_TYPE_CREDIT){
              this.documentInvoiceCredit += +document.net ?? 0;
            } else {
              this.documentInvoiceCharge += +document.net ?? 0;
            }
          });

          this.documentInvoiceNet = +this.documentInvoiceCredit - +this.documentInvoiceCharge;
          this.initialize(invoice);
          this.invoiceForm.updateValueAndValidity();
        });
    }
    this.netControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe((res) => {
        if (this.invoiceObject.partner && !this.isMandateBill()) {
          this.grossControl.setValue(this.netControl.value, { emitEvent: false });
        }
        this.calculatePayoutAmount();
      });

    // this.grossControl.valueChanges
    // .pipe()
    //   .subscribe(() => {
    //     if(!this.isMandateBill()) {
    //       this.netControl.setValue(this.netControl.value);
    //     }
    //   });

  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initialize(invoice: Invoice): void {
    of(invoice)
      .pipe(
        delay(0),
        tap(() => {
          this.invoiceForm.reset({
            number: invoice.number,
            net: invoice.mandateBill ? invoice.payoutNet : invoice.net,
            sellerCompanyName: invoice.sellerCompanyName,
            sellerNip: invoice.sellerNip,
            sellerStreet: invoice.sellerStreet,
            sellerHouseNumber: invoice.sellerHouseNumber,
            sellerApartmentNumber: invoice.sellerApartmentNumber,
            sellerPostCity: invoice.sellerPostCity,
            sellerZipCode: invoice.sellerZipCode,
            sellerCity: invoice.sellerCity,
            sellerAccountNumber: invoice.sellerAccountNumber,
            sellerEmail: invoice.sellerEmail,
            phone: invoice.sellerPhone,
            vatInvoice: invoice.vatInvoice,
            note: invoice.note,
            pdfName: invoice.fileName,
            fileName: invoice.fileName,
            createdAt: invoice.createdAt,
            gross: invoice.gross
          });
        }),
        delay(0),
        tap(() => this.initialized.next(true))
      ).subscribe();
  }

  save(): void {
    if (!this.invoiceObject.partner && !this.isMandateBill()) {
      const grossValue = Number(this.grossControl.value) || 0;
      const vatRate = Number(this.invoiceObject.vatRate) || 0;

      this.netControl.setValue(grossValue / (1 + vatRate / 100));
    }

    if (!this.invoiceForm.valid) {
      this.invoiceForm.markAllAsTouched();
      this.snack.open('Wypełnij lub popraw wszystkie wymagane pola', '', {
        duration: 2000,
      });
      return;
    }
    this.processingRequest = true;
    this.invoiceService.sendForm(this.invoiceForm.value, this.invoiceObject ? this.invoiceObject.id : null)
      .pipe( first())
      .subscribe((response: Uuid) => {
        this.snack.open(this.invoiceObject ? 'Pomyślnie zaktualizowano fakturę'
          : 'Pomyślnie utworzono fakturę', '', {
          duration: 2000,
        });
        this.processingRequest = false;
        if (!this.invoiceObject) {
          this.router.navigate(['/faktury/szczegoly', response.uuid]);
        } else {
          this.closeModification.emit();
        }
      }, (error: HttpErrorResponse) => {
        this.processingRequest = false;
        this.snack.open(this.invoiceObject ? 'Podczas aktualizacji faktury wystąpił błąd'
          : 'Podczas tworzenia faktury  wystąpił błąd', error.status.toString(), {
          duration: 2000,
        });
        if (error.status === 400) {
          this.snack.open(error.error.error.text, '400', {duration: 4000});
          this.formHelper.setBackendErrorState(this.invoiceForm, error.error.error.list);
        }
      })
    ;


  }
  cancel(): void {
    if (!this.invoiceObject) {
      this.router.navigate(['/faktury']);
    } else {
      this.closeModification.emit();
    }
  }

  fileSelected($event: FileBase64): void {
    this.fileNameControl.setValue($event?.filename);
    this.fileContentControl.setValue($event?.base64);
  }

  downloadPdf(): void {
    this.invoiceService.downloadPdf(this.invoiceObject.id);
  }

  isMandateBill(): boolean {
    return !!this.invoiceObject?.mandateBill;
  }

  calculatePayoutAmount(): number {
    const documentInvoiceNet = Number(this.documentInvoiceNet) || 0;

    if (this.isMandateBill()) {
      const payoutNet = Number(this.invoiceObject?.payoutNet) || 0;

      return payoutNet + documentInvoiceNet;
    }

    if (this.invoiceObject?.partner) {
      const netValue = Number(this.netControl?.value) || 0;

      return documentInvoiceNet + netValue;
    }

    const grossValue = Number(this.grossControl?.value) || 0;
    const vatRate = Number(this.invoiceObject?.vatRate) || 0;

    return documentInvoiceNet + (grossValue / (1 + vatRate / 100));
  }

}
